import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { createPortal } from 'react-dom';
import { Student } from '../../types';
import { matchesHangulSearch } from '../../utils/hangul';

interface StudentSearchProps {
  students: Student[];
  onStudentSelect: (student: Student) => void;
  selectedStudent: Student | null;
}

const StudentSearch: React.FC<StudentSearchProps> = ({
  students,
  onStudentSelect,
  selectedStudent,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredStudents, setFilteredStudents] = useState<Student[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: 0 });
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (searchTerm.length > 0) {
      const filtered = students.filter(student => 
        matchesHangulSearch(student.name, searchTerm)
      );
      setFilteredStudents(filtered);
      setIsOpen(true);
    } else {
      setFilteredStudents([]);
      setIsOpen(false);
    }
  }, [searchTerm, students]);

  // 드롭다운 위치 계산
  useEffect(() => {
    if (isOpen && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
        width: rect.width
      });
    }
  }, [isOpen, searchTerm]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectStudent = (student: Student) => {
    onStudentSelect(student);
    setSearchTerm(student.name);
    setIsOpen(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    if (!value.trim()) {
      onStudentSelect(null as any);
    }
  };

  const clearSearch = () => {
    setSearchTerm('');
    setIsOpen(false);
  };

  // 드롭다운 렌더링 함수
  const renderDropdown = () => {
    if (!isOpen) return null;

    const dropdownContent = (
      <div
        style={{
          position: 'absolute',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          width: dropdownPosition.width,
          zIndex: 99999
        }}
        className="bg-white rounded-xl shadow-2xl border border-gray-200 max-h-60 overflow-y-auto"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {filteredStudents.length > 0 ? (
          <>
            <div className="p-2 text-sm text-gray-500 bg-gray-50 border-b rounded-t-xl">
              검색 결과: {filteredStudents.length}개
            </div>
            {filteredStudents.map((student, index) => (
              <motion.div
                key={student.id}
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelectStudent(student);
                }}
                onMouseDown={(e) => e.stopPropagation()}
                className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 last:rounded-b-xl"
              >
                <div className="flex items-center space-x-3">
                  <span className="text-lg">👤</span>
                  <span className="font-medium text-gray-800">
                    {student.name}
                  </span>
                </div>
                <span className="px-2 py-1 rounded-full text-xs font-medium text-purple-600 bg-purple-100">
                  {searchTerm.match(/^[ㄱ-ㅎ]+$/) ? '초성' : '일치'}
                </span>
              </motion.div>
            ))}
          </>
        ) : (
          <div className="p-4 text-center rounded-xl">
            <div className="text-gray-500">검색 결과가 없습니다</div>
          </div>
        )}
      </div>
    );

    return createPortal(dropdownContent, document.body);
  };

  return (
    <div className="relative" ref={searchRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          placeholder="이름을 입력해주세요 (초성 검색 가능)"
          className="input-field pr-12"
          autoComplete="off"
        />
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          {searchTerm ? (
            <button
              onClick={clearSearch}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              ✕
            </button>
          ) : (
            <span className="text-gray-400">🔍</span>
          )}
        </div>
      </div>

      {renderDropdown()}
    </div>
  );
};

export default StudentSearch; 